import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import api, { resolveUploadUrl } from '../api';
import Post from '../components/Post';
import PremiumBadge from '../components/PremiumBadge';

import {
  User,
  Calendar,
  UserPlus,
  Twitter,
  Instagram,
  Github,
  Settings,
  Link2,
  Globe,
  Loader2,
  AlertCircle,
  Check,
  Mail
} from 'lucide-react';

// --- Constants ---
const AVATAR_COLORS = [
  'bg-gradient-to-br from-blue-500 to-purple-600',
  'bg-gradient-to-br from-green-500 to-teal-600',
  'bg-gradient-to-br from-red-500 to-pink-600',
  'bg-gradient-to-br from-yellow-500 to-orange-600',
  'bg-gradient-to-br from-indigo-500 to-purple-600',
  'bg-gradient-to-br from-pink-500 to-rose-600',
  'bg-gradient-to-br from-teal-500 to-cyan-600',
  'bg-gradient-to-br from-orange-500 to-red-800'
];
const USER_EMOJIS = ['😀', '😎', '🚀', '💎', '🌟', '🎯', '🏆', '🎨', '🦄', '🌈', '🔥', '⚡', '🌊', '🦊', '🐙', '🦋', '🐠', '🦢', '🦥', '🦦'];

// --- Custom Hook: useUserProfile ---
const useUserProfile = (userId, currentUser) => {
  const [user, setUser] = useState(null);
  const [userPosts, setUserPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchUserData = useCallback(async () => {
    if (!userId) {
      setError("Invalid user ID.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const userResponse = await api.get(`/users/${userId}`);
      const userData = userResponse.data;
      setUser(userData);

      try {
        const postsResponse = await api.get(`/community/users/${userId}/posts/`, { timeout: 20000 });
        const postsData = postsResponse.data?.items || postsResponse.data || [];
        setUserPosts(postsData);
      } catch (postErr) {
        console.warn("Could not fetch posts:", postErr.message);
        setUserPosts([]);
      }

    } catch (err) {
      console.error('Error fetching user profile:', err);
      const message = err.response?.data?.detail || err.message || 'Failed to load user profile';
      setError(message);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  return { user, setUser, userPosts, loading, error, refetch: fetchUserData };
};

// --- Sub-components ---

const ProfileHeader = ({ userName, isOwnProfile, onSettingsClick }) => (
  <header className="bg-white dark:bg-gray-800/50 backdrop-blur-sm sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700/30">
    <div className="max-w-6xl mx-auto px-4 py-3">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">
          {isOwnProfile ? 'My Profile' : `${userName}'s Profile`}
        </h1>
        {isOwnProfile && (
          <button onClick={onSettingsClick} className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors p-2 rounded-lg">
            <Settings size={20} />
          </button>
        )}
      </div>
    </div>
  </header>
);

const CoverPhotoSection = ({ user, isOwnProfile, onFollowClick, processingFollow, followingStatus, onMessageClick }) => {
  const getUserAvatar = useCallback((user) => {
    if (user.avatar) return resolveUploadUrl(user.avatar);
    const colorIndex = user.id ? user.id % AVATAR_COLORS.length : 0;
    return AVATAR_COLORS[colorIndex];
  }, []);

  const hasAvatar = useCallback((user) => {
    return !!user.avatar;
  }, []);

  const getUserEmoji = useCallback((user) => {
    if (user.emoji) return user.emoji;
    const emojiIndex = user.id ? user.id % USER_EMOJIS.length : 0;
    return USER_EMOJIS[emojiIndex];
  }, []);

  return (
    <div className="h-24 md:h-32 bg-gradient-to-r from-blue-500 to-purple-600 relative rounded-xl overflow-hidden mb-6">
      <div className="absolute top-4 left-4">
        <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-white text-xl font-bold overflow-hidden ${getUserAvatar(user)}`}>
          {hasAvatar(user) ? (
            <img src={getUserAvatar(user)} alt={user.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-2xl md:text-3xl">{getUserEmoji(user)}</span>
          )}
        </div>
      </div>

      {!isOwnProfile && (
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button
            onClick={onMessageClick}
            className="p-2 rounded-full bg-white/90 text-blue-600 hover:bg-white transition-colors shadow-md"
            title="Send Message"
          >
            <Mail size={20} />
          </button>

          <button
            onClick={onFollowClick}
            disabled={processingFollow}
            className={`p-2 rounded-full transition-all duration-200 flex items-center justify-center ${
              processingFollow ? 'opacity-50 cursor-not-allowed' : ''
            } ${followingStatus ? 'bg-white/20 text-white' : 'bg-white/90 text-blue-600 hover:bg-white'}`}
          >
            {processingFollow ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus size={20} className={followingStatus ? 'text-white' : 'text-blue-600'} />}
          </button>
        </div>
      )}
    </div>
  );
};

const ProfileInfo = ({ user, userPosts }) => {
  const [copied, setCopied] = useState(false);
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const renderBadge = (isPremium, size = "w-5 h-5") => {
    if (!isPremium) return null;
    return <PremiumBadge size={16} className={size} />;
  };

  return (
    <div className="mb-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{user.name}</h2>
            {user.verified && <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center"><Check size={16} className="text-white" /></div>}
            {renderBadge(user.is_premium, "w-7 h-7")}
          </div>
          {user.username && (
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">@{user.username}</p>
          )}
          <p className="text-gray-600 dark:text-gray-300 text-base md:text-lg mb-2 min-h-[28px]">{user.bio || "No bio provided."}</p>
          <div className="flex items-center gap-4">
            <p className="text-gray-500 dark:text-gray-400 text-sm">Member since {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}</p>
            <button onClick={handleCopyLink} className="text-gray-400 hover:text-blue-500 dark:text-gray-500 dark:hover:text-blue-400 transition-colors text-sm flex items-center gap-1">
              {copied ? <><Check size={14} /><span>Link copied</span></> : <><Link2 size={14} /><span>Copy Profile Link</span></>}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-center"><p className="text-xl font-bold text-gray-900 dark:text-white">{userPosts.length}</p><p className="text-gray-500 dark:text-gray-400 text-xs">Posts</p></div>
          <div className="text-center"><p className="text-xl font-bold text-gray-900 dark:text-white">{user.followers_count || 0}</p><p className="text-gray-500 dark:text-gray-400 text-xs">Followers</p></div>
          <div className="text-center"><p className="text-xl font-bold text-gray-900 dark:text-white">{user.following_count || 0}</p><p className="text-gray-500 dark:text-gray-400 text-xs">Following</p></div>
        </div>
      </div>
    </div>
  );
};

const SocialLinks = ({ user }) => {
  const socialLinks = [
    { icon: Twitter, key: 'twitter', label: 'Twitter', color: 'blue' },
    { icon: Instagram, key: 'instagram', label: 'Instagram', color: 'pink' },
    { icon: Github, key: 'github', label: 'GitHub', color: 'gray' },
    { icon: Globe, key: 'website', label: 'Website', color: 'green' }
  ];
  const hasLinks = socialLinks.some(link => user[link.key]);
  if (!hasLinks) return null;

  return (
    <div className="mb-8">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2"><Link2 size={20} /> Social Links</h3>
      <div className="flex flex-wrap gap-3 justify-center md:justify-start">
        {socialLinks.map(({ icon: Icon, key, label, color }) => {
          if (!user[key]) return null;
          const href = key === 'website' ? user[key] : `https://${key}.com/${user[key]}`;
          return (
            <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="group relative overflow-hidden bg-gray-100 dark:bg-gray-800 rounded-xl p-4 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200 flex flex-col items-center justify-center border border-gray-200 dark:border-transparent">
              <Icon className={`w-6 h-6 text-gray-500 group-hover:text-${color}-500 dark:text-gray-400 transition-colors`} />
              <span className={`text-xs text-gray-500 group-hover:text-${color}-500 dark:text-gray-400 mt-2 transition-colors`}>{label}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

// --- Main Component ---
export default function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUserId, currentUser: currentUserFromContext } = useOutletContext();
  const { currentUser: currentUserFromAuth, refreshUser } = useAuth();

  const [processingLikes, setProcessingLikes] = useState(new Set());
  const [processingFollow, setProcessingFollow] = useState(false);
  const [followingStatus, setFollowingStatus] = useState(false);
  const [processingPostFollowId, setProcessingPostFollowId] = useState(null);

  const currentUser = useMemo(() => currentUserFromAuth || currentUserFromContext, [currentUserFromAuth, currentUserFromContext]);

  const actualUserId = useMemo(() => {
    if (userId === "me" || !userId) {
      return currentUser?.id || currentUserId;
    }
    const parsedId = parseInt(userId, 10);
    return isNaN(parsedId) ? null : parsedId;
  }, [userId, currentUser, currentUserId]);

  const isOwnProfile = useMemo(() => {
    return currentUser && actualUserId && String(currentUser.id) === String(actualUserId);
  }, [currentUser, actualUserId]);

  const { user, setUser, userPosts, loading, error, refetch } = useUserProfile(actualUserId, currentUser);

  // --- PULL TO REFRESH INTEGRATION ---
  // Use the hook with the refetch function
  const containerRef = usePullToRefresh(refetch);

  // Sync follow status
  useEffect(() => {
    if (user) {
      setFollowingStatus(user.is_following || false);
    }
  }, [user]);

  // Notification Scroll Effect
  useEffect(() => {
    const targetPostId = searchParams.get('postId');
    if (!loading && targetPostId && userPosts.length > 0) {
      const timer = setTimeout(() => {
        const element = document.getElementById(`post-${targetPostId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2', 'dark:ring-offset-gray-900');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2', 'dark:ring-offset-gray-900');
            searchParams.delete('postId');
            setSearchParams(searchParams, { replace: true });
          }, 3000);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, userPosts, searchParams, setSearchParams]);

  // --- HANDLERS ---

  const handleMessageUser = useCallback((targetUserId) => {
    navigate(`/dashboard/messages/${targetUserId}`);
  }, [navigate]);

  const handleProfileFollowClick = useCallback(async () => {
    if (!user || processingFollow) return;
    
    setProcessingFollow(true);
    
    try {
      if (followingStatus) {
        await api.delete(`/community/follow/${actualUserId}`);
      } else {
        await api.post(`/community/follow/${actualUserId}`);
      }

      setFollowingStatus(prev => !prev);
      await Promise.all([refetch(), refreshUser()]);
      
    } catch (error) {
      console.error("Failed to follow/unfollow user:", error);
      alert("Could not update follow status.");
    } finally {
      setProcessingFollow(false);
    }
  }, [user, processingFollow, followingStatus, actualUserId, refetch, refreshUser]);

  const handleFollowUser = useCallback(async (userOrId) => {
    const targetUserId = typeof userOrId === 'object' ? userOrId.id : userOrId;
    if (!targetUserId || processingPostFollowId === targetUserId) return;

    setProcessingPostFollowId(targetUserId);
    try {
      await api.post(`/community/follow/${targetUserId}`);
      await refreshUser();
      await refetch();
    } catch (err) {
      console.error("Failed to follow", err);
    } finally {
      setProcessingPostFollowId(null);
    }
  }, [processingPostFollowId, refetch, refreshUser]);

  const handleUnfollowUser = useCallback(async (userOrId) => {
    const targetUserId = typeof userOrId === 'object' ? userOrId.id : userOrId;
    if (!targetUserId || processingPostFollowId === targetUserId) return;

    setProcessingPostFollowId(targetUserId);
    try {
      await api.delete(`/community/follow/${targetUserId}`);
      await refreshUser();
      await refetch();
    } catch (err) {
      console.error("Failed to unfollow", err);
    } finally {
      setProcessingPostFollowId(null);
    }
  }, [processingPostFollowId, refetch, refreshUser]);

  const hasUserLikedPost = useCallback((postId) => {
    const post = userPosts.find(p => p.id === postId);
    return post?.has_liked || post?.hasLiked || false;
  }, [userPosts]);

  const handleLikePost = useCallback(async (postId) => {
    if (processingLikes.has(postId)) return;
    setProcessingLikes(prev => new Set(prev).add(postId));
    try {
      const hasLiked = hasUserLikedPost(postId);
      if (hasLiked) {
        await api.delete(`/community/posts/${postId}/like`);
      } else {
        await api.post(`/community/posts/${postId}/like`);
      }
      refetch();
    } catch (error) {
      console.error("Failed to like post:", error);
    } finally {
      setTimeout(() => {
        setProcessingLikes(prev => {
          const newSet = new Set(prev);
          newSet.delete(postId);
          return newSet;
        });
      }, 500);
    }
  }, [processingLikes, hasUserLikedPost, refetch]);

  const handleDeletePost = useCallback(async (postId) => {
    try {
      await api.delete(`/community/posts/${postId}`);
      refetch();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert("Failed to delete post");
    }
  }, [refetch]);

  const handleDeleteComment = useCallback(async (commentId) => {
    try {
      await api.delete(`/community/comments/${commentId}`);
      refetch();
    } catch (err) {
      console.error("Failed to delete comment", err);
      alert("Could not delete comment.");
    }
  }, [refetch]);

  const handleRepost = useCallback(async (postId) => {
    try {
      await api.post(`/community/posts/${postId}/repost`);
      refetch();
    } catch (error) {
      console.error("Failed to repost", error);
    }
  }, [refetch]);

  const handleUnrepost = useCallback(async (postId) => {
    try {
      await api.delete(`/community/posts/${postId}/repost`);
      refetch();
    } catch (error) {
      console.error("Failed to unrepost", error);
    }
  }, [refetch]);

  const handleUserClick = (uid) => {
    navigate(`/dashboard/profile/${uid}`);
  };

  const handleSettingsClick = useCallback(() => {
    navigate('/dashboard/settings/profile');
  }, [navigate]);

  const handleAddComment = useCallback(async (postId, content, parentId) => {
    try {
      await api.post(`/community/posts/${postId}/comments`, { content, parent_id: parentId });
      refetch();
    } catch (error) {
      console.error("Failed to add comment", error);
    }
  }, [refetch]);

  const handleReportPost = useCallback(async (postId) => {
    const reason = window.prompt("Please enter the reason for reporting this post:");
    if (reason === null) return;
    if (!reason || !reason.trim()) {
      alert("Please provide a reason for the report.");
      return;
    }
    try {
      await api.post(`/community/posts/${postId}/report`, { reason: reason.trim() });
      alert("Post reported successfully. Our team will review it.");
    } catch (err) {
      console.error("Failed to report post", err);
      if (err.response?.status === 400) {
        alert("You have already reported this post.");
      } else {
        alert("Failed to submit report.");
      }
    }
  }, []);

  const normalizePost = (post) => ({
    ...post,
    image_url: post.image_url || post.image || null,
    authorName: post.author_name || post.authorName,
    authorAvatar: post.author_avatar || post.authorAvatar,
    userId: post.user_id || post.userId,
    authorId: post.user_id || post.authorId,
    isAuthorPremium: post.author_is_premium || post.isAuthorPremium || false,
    likes_count: post.likes_count || post.like_count || 0,
    comments_count: post.comments_count || post.comment_count || 0,
    has_liked: post.has_liked || post.hasLiked || false,
  });

  const renderBadge = (isPremium, size = "w-5 h-5") => {
    if (!isPremium) return null;
    return <PremiumBadge size={16} className={size} />;
  };

  // --- RENDER STATES ---
  if (!currentUser) return <div className="flex justify-center items-center p-8 min-h-screen bg-slate-50 dark:bg-gray-900"><Loader2 className="w-12 h-12 text-blue-500 animate-spin" /></div>;
  if (loading) return <div className="flex justify-center items-center p-8 min-h-screen bg-slate-50 dark:bg-gray-900"><div className="flex flex-col items-center justify-center"><Loader2 className="w-12 h-12 text-blue-500 animate-spin" /><p className="mt-4 text-gray-500 dark:text-gray-400">Loading profile...</p></div></div>;
  if (error) return <div className="flex justify-center items-center p-8 min-h-screen bg-slate-50 dark:bg-gray-900"><div className="text-center max-w-md"><AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" /><p className="text-xl text-gray-900 dark:text-white mb-2">Something went wrong</p><p className="text-gray-500 dark:text-gray-400 mb-4">{error}</p><button onClick={() => navigate('/dashboard/community')} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Back to Community</button></div></div>;
  if (!user) return <div className="flex justify-center items-center p-8 min-h-screen bg-slate-50 dark:bg-gray-900"><div className="text-center max-w-md"><User className="w-16 h-16 text-gray-400 mx-auto mb-4" /><p className="text-xl text-gray-900 dark:text-white mb-2">User not found</p><p className="text-gray-500 dark:text-gray-400 mb-4">The user you're looking for doesn't exist.</p><button onClick={() => navigate('/dashboard/community')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Back to Community</button></div></div>;

  return (
    <div 
      ref={containerRef}
      className="no-scrollbar h-full bg-slate-50 dark:bg-gray-900 transition-colors duration-300 overflow-y-auto overflow-x-hidden"
    >
      <ProfileHeader userName={user.name} isOwnProfile={isOwnProfile} onSettingsClick={handleSettingsClick} />
      <div className="max-w-6xl mx-auto p-4 md:p-6 pb-24">
        <CoverPhotoSection 
          user={user} 
          isOwnProfile={isOwnProfile} 
          onFollowClick={handleProfileFollowClick} 
          processingFollow={processingFollow} 
          followingStatus={followingStatus}
          onMessageClick={() => handleMessageUser(actualUserId)}
        />
        <div className="px-4 md:px-8">
          <ProfileInfo user={user} userPosts={userPosts} />
          <SocialLinks user={user} />
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Calendar size={20} /> Recent Posts</h2>
            </div>
            <div className="space-y-6">
              {userPosts.length > 0 ? (
                userPosts.map(post => {
                  const normalizedPost = normalizePost(post);
                  const isAuthorProfileOwner = String(normalizedPost.userId) === String(actualUserId);
                  const isFollowingAuthor = isAuthorProfileOwner 
                    ? followingStatus 
                    : (normalizedPost.is_following_author || false);
                  const isPostAuthorCurrentUser = String(currentUser?.id) === String(normalizedPost.userId);
                  const highlightPostId = searchParams.get('postId');

                  return (
                    <div key={normalizedPost.id} id={`post-${normalizedPost.id}`} className="transition-all duration-300 rounded-xl">
                      <Post
                        post={normalizedPost}
                        currentUser={currentUser}
                        onLike={handleLikePost}
                        onDelete={handleDeletePost}
                        onUserClick={handleUserClick}
                        onAddComment={handleAddComment}
                        onDeleteComment={handleDeleteComment}
                        onRepost={handleRepost}
                        onUnrepost={handleUnrepost}
                        onFollowUser={handleFollowUser}
                        onUnfollowUser={handleUnfollowUser}
                        isFollowingAuthor={isFollowingAuthor}
                        onReport={handleReportPost}
                        onMessageUser={handleMessageUser}
                        isProcessingLike={processingLikes.has(normalizedPost.id)}
                        isOwnPost={isPostAuthorCurrentUser}
                        renderBadge={renderBadge}
                        highlight={String(normalizedPost.id) === String(highlightPostId)}
                      />
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="w-16 h-16 mx-auto mb-4 opacity-50"><Calendar size={32} /></div>
                  <p>{isOwnProfile ? "You haven't posted anything yet." : "This user hasn't posted anything yet."}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}